import React from 'react';
import styled from 'styled-components';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import Navbar, { NAVBAR_HEIGHT } from '../../../components/Navbar';
import Step from '../../../components/AssessmentTest/Step';
import { TestIntro } from '../../../components/AssessmentTest/TestIntro';
import TestOutro from '../../../components/AssessmentTest/TestOutro';
import TestTimeout from '../../../components/AssessmentTest/TestTimeout';
import {
  AssessmentTestContext,
  AssessmentTestProvider,
} from '../../../contexts/assessment-test';
import type { AssessmentTestResponse } from '../../../api/teams';

const PREVIEW_STORAGE_KEY = 'enki:preview:assessment-test';
const PREVIEW_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  flex: 1;
  gap: 16px;
  overflow-y: auto;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  gap: 16px;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 18px;
`;

export default function CMSPreviewAssessmentTest() {
  const [previewData, setPreviewData] =
    React.useState<AssessmentTestResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // enki ships @formkit/auto-animate 0.8.0, which re-runs on a new options object
  // every render (infinite loop). Memoize the options like enki's own AssessmentTest
  // page (src/pages/AssessmentTest.tsx) does.
  const animOptions = React.useRef({
    duration: 500,
    easing: 'ease-in-out',
  }).current;
  const [animRef] = useAutoAnimate(animOptions);

  // Load preview data and check expiry
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(PREVIEW_STORAGE_KEY);
      if (!raw) {
        setError(
          'No preview data found. Open the preview from the editor page.'
        );
        return;
      }

      const parsed = JSON.parse(raw);

      if (
        parsed._createdAt &&
        Date.now() - parsed._createdAt > PREVIEW_MAX_AGE_MS
      ) {
        localStorage.removeItem(PREVIEW_STORAGE_KEY);
        setError(
          'Preview data has expired. Open a new preview from the editor.'
        );
        return;
      }

      if (!parsed.steps || !Array.isArray(parsed.steps)) {
        setError('Invalid preview data: steps must be an array.');
        return;
      }

      // Synthetic session response for the preview; negative ids never collide
      // with real DB rows, and there is no submission (the test is unstarted).
      const mockResponse = {
        assessmentTestId: -1,
        assessmentTestSessionId: -1,
        steps: parsed.steps,
        answers: [],
        startedAt: null,
        finishedAt: null,
        companyId: -1,
        finishReason: null,
      } as unknown as AssessmentTestResponse;

      setPreviewData(mockResponse);
    } catch (e) {
      setError(`Failed to load preview data: ${(e as Error).message}`);
    }
  }, []);

  // Clear preview data when page is closed or navigated away
  React.useEffect(() => {
    const cleanup = () => localStorage.removeItem(PREVIEW_STORAGE_KEY);
    window.addEventListener('beforeunload', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
  }, []);

  return (
    <>
      <Navbar
        isPreview
        breadCrumbs={[
          {
            label: 'Skills Lab',
            url: null,
          },
        ]}
      />
      {error && <ErrorWrapper>{error}</ErrorWrapper>}
      {previewData && (
        <AssessmentTestProvider test={previewData}>
          <Content ref={animRef}>
            <PreviewAssessmentTestContent />
          </Content>
        </AssessmentTestProvider>
      )}
    </>
  );
}

function PreviewAssessmentTestContent() {
  const [state] = React.useContext(AssessmentTestContext);

  return (
    <>
      {state.screen.type === 'INTRO' && <TestIntro key="test-intro" />}
      {state.screen.type === 'OUTRO' && <TestOutro key="test-outro" />}
      {state.screen.type === 'TIMEOUT' && <TestTimeout key="test-timeout" />}
      {state.screen.type === 'STEP' && (
        <Step key={`test-step-${state.screen.stepId}`} />
      )}
    </>
  );
}
